import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Users,
  Mail,
  Phone,
  Building2,
  BadgeCheck,
  IdCard,
  RefreshCcw,
  Search,
} from "lucide-react";
import { employeeService } from "../services/api";

const STATUS_COLORS = {
  Active:
    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  Confirmed:
    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  Probation:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  "On Leave":
    "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",
  Inactive:
    "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700",
  Resigned:
    "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
  Relieved:
    "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
  Terminated:
    "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
};

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function isDirectPhotoUrl(value) {
  if (typeof value !== "string" || !value) return false;
  return (
    value.startsWith("data:") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

function MyTeam() {
  const [reportees, setReportees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [photoUrlMap, setPhotoUrlMap] = useState(() => new Map());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchReportees = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await employeeService.getMyReportees();
      if (!mountedRef.current) return;
      if (resp?.success) {
        setReportees(Array.isArray(resp.data) ? resp.data : []);
      } else {
        setReportees([]);
        setError(resp?.message || "Failed to load your team");
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setReportees([]);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load your team"
      );
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportees();
  }, [fetchReportees]);

  // Resolve signed photo URLs for employees whose `photo` is an R2 path.
  useEffect(() => {
    if (!reportees.length) return;
    const toFetch = [];
    for (const emp of reportees) {
      if (!emp?.id) continue;
      const raw = emp.photo;
      if (!raw || typeof raw !== "string") continue;
      if (isDirectPhotoUrl(raw)) continue;
      if (photoUrlMap.get(emp.id)) continue;
      toFetch.push(emp.id);
    }
    if (!toFetch.length) return;

    let cancelled = false;
    Promise.all(
      toFetch.map((id) =>
        employeeService
          .getEmployeePhotoUrl(id)
          .then((resp) => ({
            id,
            url: resp?.url || resp?.data?.url || "",
          }))
          .catch(() => ({ id, url: "" }))
      )
    ).then((results) => {
      if (cancelled || !mountedRef.current) return;
      setPhotoUrlMap((prev) => {
        const next = new Map(prev);
        results.forEach((r) => {
          if (r?.id && r?.url) next.set(r.id, r.url);
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [reportees, photoUrlMap]);

  const filteredReportees = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return reportees;
    return reportees.filter((r) => {
      const haystack = [
        r.fullName,
        r.empCode,
        r.email,
        r.phone,
        r?.designation?.name,
        r?.department?.name,
        r?.branch?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [reportees, searchTerm]);

  const getPhotoSrc = (emp) => {
    const raw = emp?.photo;
    if (isDirectPhotoUrl(raw)) return raw;
    return photoUrlMap.get(emp?.id) || "";
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-8xl h-full bg-white rounded-lg shadow-md overflow-hidden">
        <div className="w-full p-6">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-redhalo-red/10 text-redhalo-red dark:bg-redhalo-mint/15 dark:text-redhalo-mint">
              <Users size={20} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                My Team
              </h1>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {loading
                  ? "Loading your reportees…"
                  : `${filteredReportees.length} of ${reportees.length} member${
                      reportees.length === 1 ? "" : "s"
                    } report to you`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by name, code, email…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 w-64 rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm text-gray-900 outline-none transition focus:border-redhalo-red focus:ring-1 focus:ring-redhalo-red/30 dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-100"
                aria-label="Search reportees"
              />
            </div>
            <button
              type="button"
              onClick={fetchReportees}
              disabled={loading}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-200 dark:hover:bg-[#2a2a2a]"
              title="Refresh"
            >
              <RefreshCcw
                size={14}
                className={loading ? "animate-spin" : ""}
              />
              Refresh
            </button>
          </div>
        </div>

        {error && !loading && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        {loading ? (
          <SkeletonGrid />
        ) : filteredReportees.length === 0 ? (
          <EmptyState
            hasReportees={reportees.length > 0}
            hasSearch={Boolean(searchTerm.trim())}
            onClearSearch={() => setSearchTerm("")}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredReportees.map((emp) => (
              <ReporteeCard
                key={emp.id}
                employee={emp}
                photoSrc={getPhotoSrc(emp)}
              />
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function ReporteeCard({ employee, photoSrc }) {
  const [imgError, setImgError] = useState(false);
  const statusKey = employee?.employmentStatus || "";
  const statusClass =
    STATUS_COLORS[statusKey] ||
    "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700";

  const showImage = Boolean(photoSrc) && !imgError;

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-redhalo-red/40 hover:shadow-md dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:hover:border-redhalo-mint/40">
      <div className="flex items-start gap-3">
        <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-redhalo-red/15 to-redhalo-mint/20 ring-1 ring-gray-200 dark:ring-[#3C3C4C]">
          {showImage ? (
            <img
              src={photoSrc}
              alt={employee?.fullName || "Employee"}
              className="h-full w-full object-cover"
              onError={() => setImgError(true)}
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-base font-semibold text-redhalo-red dark:text-redhalo-mint">
              {getInitials(employee?.fullName || employee?.empCode)}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3
            className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100"
            title={employee?.fullName || ""}
          >
            {employee?.fullName || "—"}
          </h3>
          <p
            className="mt-0.5 truncate text-xs font-medium text-gray-600 dark:text-gray-400"
            title={employee?.designation?.name || ""}
          >
            {employee?.designation?.name || "—"}
          </p>
          {statusKey && (
            <span
              className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusClass}`}
            >
              <BadgeCheck size={10} />
              {statusKey}
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-3 text-xs dark:border-[#3C3C4C]">
        <DetailRow
          icon={<IdCard size={12} />}
          label={employee?.empCode || "—"}
          title="Employee Code"
        />
        <DetailRow
          icon={<Building2 size={12} />}
          label={employee?.department?.name || "—"}
          title="Department"
        />
        <DetailRow
          icon={<Mail size={12} />}
          label={employee?.email || "—"}
          title={employee?.email || ""}
          truncate
        />
        <DetailRow
          icon={<Phone size={12} />}
          label={employee?.phone || "—"}
          title="Phone"
        />
      </div>
    </div>
  );
}

function DetailRow({ icon, label, title, truncate = false }) {
  return (
    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-[#2a2a2a] dark:text-gray-400">
        {icon}
      </span>
      <span
        className={`${truncate ? "truncate" : ""} text-gray-700 dark:text-gray-300`}
        title={title}
      >
        {label}
      </span>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-2xl border border-gray-200 bg-white p-4 dark:border-[#3C3C4C] dark:bg-[#1D1D1D]"
        >
          <div className="flex items-start gap-3">
            <div className="h-14 w-14 flex-shrink-0 rounded-xl bg-gray-200 dark:bg-[#2a2a2a]" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-[#2a2a2a]" />
              <div className="h-2.5 w-1/2 rounded bg-gray-200 dark:bg-[#2a2a2a]" />
              <div className="h-4 w-16 rounded bg-gray-200 dark:bg-[#2a2a2a]" />
            </div>
          </div>
          <div className="mt-3 space-y-2 border-t border-gray-100 pt-3 dark:border-[#3C3C4C]">
            <div className="h-2.5 w-full rounded bg-gray-200 dark:bg-[#2a2a2a]" />
            <div className="h-2.5 w-5/6 rounded bg-gray-200 dark:bg-[#2a2a2a]" />
            <div className="h-2.5 w-3/4 rounded bg-gray-200 dark:bg-[#2a2a2a]" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ hasReportees, hasSearch, onClearSearch }) {
  const isSearchEmpty = hasReportees && hasSearch;
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white/60 py-12 text-center dark:border-[#3C3C4C] dark:bg-[#1D1D1D]/50">
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-redhalo-red/10 text-redhalo-red dark:bg-redhalo-mint/15 dark:text-redhalo-mint">
        <Users size={24} />
      </div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        {isSearchEmpty ? "No matches found" : "No reportees yet"}
      </h3>
      <p className="mt-1 max-w-md px-4 text-xs text-gray-500 dark:text-gray-400">
        {isSearchEmpty
          ? "Try a different search term."
          : "Employees whose reporting manager is set to you will appear here."}
      </p>
      {isSearchEmpty && (
        <button
          type="button"
          onClick={onClearSearch}
          className="mt-3 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-200 dark:hover:bg-[#2a2a2a]"
        >
          Clear search
        </button>
      )}
    </div>
  );
}

export default MyTeam;
